import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, Loader2, Target, TrendingUp } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const XAIPredictorPage = () => {
  const [form, setForm] = useState({
    prediction_type: "win_probability",
    case_type: "personal_injury",
    jurisdiction: "california",
    venue: "",
    judge_name: "",
    insurance_carrier: "",
    evidence_strength: "moderate",
    liability_clarity: "clear",
    medical_specials: 0
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const predict = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/intelligence/xai/predict`, form);
      setResult(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="xai-predictor-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-6xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>XAI Predictor</h1>
            <Badge className="bg-gradient-to-r from-violet-600 to-indigo-600">EXPLAINABLE AI</Badge>
          </div>
          <p className="text-muted-foreground">Get predictions with full factor attribution - see WHY, not just what</p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Prediction Input</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Prediction Type</Label>
                  <Select value={form.prediction_type} onValueChange={v => setForm({...form, prediction_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="win_probability">Win Probability</SelectItem>
                      <SelectItem value="settlement_range">Settlement Range</SelectItem>
                      <SelectItem value="motion_success">Motion Success</SelectItem>
                      <SelectItem value="time_to_resolution">Time to Resolution</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Case Type</Label>
                  <Select value={form.case_type} onValueChange={v => setForm({...form, case_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="personal_injury">Personal Injury</SelectItem>
                      <SelectItem value="employment">Employment</SelectItem>
                      <SelectItem value="contract">Contract</SelectItem>
                      <SelectItem value="medical_malpractice">Medical Malpractice</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Jurisdiction</Label>
                  <Input value={form.jurisdiction} onChange={e => setForm({...form, jurisdiction: e.target.value})} placeholder="e.g., california" />
                </div>
                <div>
                  <Label>Venue</Label>
                  <Input value={form.venue} onChange={e => setForm({...form, venue: e.target.value})} placeholder="e.g., LA Superior Court" />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Judge Name</Label>
                  <Input value={form.judge_name} onChange={e => setForm({...form, judge_name: e.target.value})} placeholder="e.g., Judge Smith" />
                </div>
                <div>
                  <Label>Insurance Carrier</Label>
                  <Input value={form.insurance_carrier} onChange={e => setForm({...form, insurance_carrier: e.target.value})} placeholder="e.g., State Farm" />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Evidence Strength</Label>
                  <Select value={form.evidence_strength} onValueChange={v => setForm({...form, evidence_strength: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="strong">Strong</SelectItem>
                      <SelectItem value="moderate">Moderate</SelectItem>
                      <SelectItem value="weak">Weak</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Liability</Label>
                  <Select value={form.liability_clarity} onValueChange={v => setForm({...form, liability_clarity: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="clear">Clear</SelectItem>
                      <SelectItem value="disputed">Disputed</SelectItem>
                      <SelectItem value="complex">Complex</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {form.prediction_type === "settlement_range" && (
                <div>
                  <Label>Medical Specials ($)</Label>
                  <Input type="number" value={form.medical_specials} onChange={e => setForm({...form, medical_specials: parseFloat(e.target.value) || 0})} />
                </div>
              )}
              
              <Button onClick={predict} disabled={loading} className="w-full">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Target className="h-4 w-4 mr-2" />}
                Generate Prediction
              </Button>
            </CardContent>
          </Card>
          
          {result && (
            <div className="space-y-4" data-testid="xai-results">
              {/* Prediction Result */}
              <Card className="border-2 border-violet-200">
                <CardContent className="p-6 text-center">
                  <p className="text-sm text-muted-foreground mb-2">{result.prediction_type.replace(/_/g, ' ').toUpperCase()}</p>
                  <p className="text-4xl font-bold text-violet-700 mb-2">{result.prediction_label}</p>
                  <p className="text-sm text-muted-foreground">Confidence: {Math.round(result.confidence_level * 100)}%</p>
                  <p className="text-xs text-muted-foreground mt-2">{result.cases_analyzed.toLocaleString()} cases analyzed • {result.data_freshness}</p>
                </CardContent>
              </Card>
              
              {/* Factor Attribution */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Factor Attribution</CardTitle>
                  <CardDescription>Why this prediction? Here are the contributing factors:</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {result.factors?.map((factor, i) => (
                      <div key={i} className={`p-3 rounded-lg border ${factor.direction === 'positive' ? 'bg-emerald-50 border-emerald-200' : factor.direction === 'negative' ? 'bg-red-50 border-red-200' : 'bg-slate-50'}`}>
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium">{factor.factor_name}</span>
                          <div className="flex items-center gap-2">
                            <Badge variant={factor.direction === 'positive' ? 'default' : factor.direction === 'negative' ? 'destructive' : 'secondary'} className="text-xs">
                              {factor.direction === 'positive' ? '+' : factor.direction === 'negative' ? '-' : ''}{factor.impact_percentage}%
                            </Badge>
                            <span className="text-xs text-muted-foreground">{Math.round(factor.confidence * 100)}% conf</span>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground">{factor.explanation}</p>
                        {factor.sample_size && <p className="text-xs text-muted-foreground mt-1">Based on {factor.sample_size} cases</p>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              {/* Improvement Recommendations */}
              {result.improvement_opportunities?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-emerald-500" />
                      Improvement Opportunities
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {result.improvement_opportunities.map((rec, i) => (
                        <div key={i} className="p-3 rounded-lg border bg-blue-50 border-blue-200">
                          <div className="flex justify-between items-start mb-1">
                            <span className="font-medium">{rec.factor}</span>
                            <Badge variant="outline" className={rec.priority === 'high' ? 'border-red-300 text-red-700' : 'border-amber-300 text-amber-700'}>{rec.priority}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-1">{rec.recommendation}</p>
                          <p className="text-sm text-emerald-700 font-medium">{rec.potential_improvement}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </main>
  );
};


export default XAIPredictorPage;
